import { useState } from 'react';
import { c, chart } from '../../theme';
import { fmtMoney, fmtCyds, fmtRate, fmtPct } from './format';

// Where each cost center stands, on whichever measure is asked for.
//
// The Summary table below reports the same figures; this ranks them, which is
// the question a reader actually arrives with -- which yards are underpriced --
// and the one a column of numbers in workbook order cannot answer.
//
// The two KINDS of measure are drawn differently on purpose:
//
//   Rates (Rev$/Cyd, Participation) put every cost center on one scale, so
//   ranking is a fair comparison and the company average is a real line to sit
//   above or below. Bars below it are drawn back, so "who is under" reads
//   without counting.
//
//   Totals (Revenue, Monthly Cyds) only rank cost centers by SIZE -- Riverside
//   would lead every week for as long as it is the biggest yard. Shown as share
//   of the company instead, with no average line: the mean of seven shares is
//   arithmetic, not a benchmark.
//
// Unresolved lines are left out. They are in the table and in the totals, but
// they are not a cost center and cannot be ranked as one.
const MEASURES = {
  rev_per_cyd: {
    label: 'Rev$/Cyd', title: 'Rev$/Cyd by cost center', kind: 'rate',
    value: (r) => r.rev_per_cyd, fmt: fmtRate,
  },
  participation_rate: {
    label: 'Participation', title: 'Participation by cost center', kind: 'rate',
    value: (r) => r.participation_rate, fmt: (v) => fmtPct(v, 1),
  },
  revenue: {
    label: 'Revenue', title: 'Share of revenue', kind: 'share',
    value: (r) => r.revenue, fmt: fmtMoney,
  },
  volume: {
    label: 'Monthly Cyds', title: 'Share of monthly cyds', kind: 'share',
    value: (r) => r.volume, fmt: fmtCyds,
  },
};

export default function SalesRanking({ rows, grandTotal, selected, onSelect }) {
  const [measure, setMeasure] = useState('rev_per_cyd');
  const m = MEASURES[measure];

  const ranked = rows
    .filter((r) => r.cost_center !== null && m.value(r) !== null && Number.isFinite(m.value(r)))
    .map((r) => ({ ...r, v: m.value(r) }))
    .sort((a, b) => b.v - a.v);

  if (!ranked.length) return null;

  const companyValue = m.value(grandTotal);
  const top = Math.max(...ranked.map((r) => r.v), m.kind === 'rate' ? (companyValue || 0) : 0);
  // Headroom on a rate so the company line never sits on the panel edge; a
  // share bar runs to the full width it earns.
  const scale = m.kind === 'rate' ? top * 1.08 : top * 1.02;
  const width = (v) => `${Math.max(0, (v / scale) * 100)}%`;

  return (
    <div>
      <div style={s.head}>
        <div>
          <div style={s.title}>{m.title}</div>
          <div style={s.sub}>
            {m.kind === 'rate'
              ? `Company average ${m.fmt(companyValue)} — bars left of the line earn below it`
              : 'Each bar is this cost center’s share of the company; totals rank by size, so there is no average to beat'}
          </div>
        </div>
        <div style={s.chips} role="group" aria-label="Rank by">
          {Object.entries(MEASURES).map(([key, def]) => (
            <button
              key={key} type="button" aria-pressed={measure === key}
              onClick={() => setMeasure(key)}
              style={{ ...s.chip, ...(measure === key ? s.chipOn : null) }}
            >
              {def.label}
            </button>
          ))}
        </div>
      </div>

      <div style={s.rows}>
        {ranked.map((r) => {
          const isSelected = selected === r.cost_center;
          return (
            <button
              key={r.cost_center}
              type="button"
              onClick={() => onSelect(isSelected ? null : r.cost_center)}
              aria-pressed={isSelected}
              title={`Chart ${r.cost_center}`}
              style={{ ...s.row, ...(isSelected ? s.rowOn : null) }}
            >
              <span style={s.name}>{r.cost_center}</span>
              <span style={s.track}>
                <span style={{
                  ...s.fill,
                  width: width(r.v),
                  background: m.kind === 'rate' && companyValue && r.v < companyValue ? chart.deselected : chart.emphasis,
                }}
                />
                {m.kind === 'rate' && companyValue ? (
                  <span style={{ ...s.avg, left: width(companyValue) }} />
                ) : null}
              </span>
              <span style={s.value}>
                {m.fmt(r.v)}
                {m.kind === 'share' && m.value(grandTotal) ? (
                  <small style={s.share}>{fmtPct(r.v / m.value(grandTotal), 1)} of total</small>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const s = {
  head:  { display: 'flex', gap: '12px', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', padding: '14px 16px 0' },
  title: { fontSize: '13px', fontWeight: 600, color: c.text },
  sub:   { fontSize: '11px', color: c.textFaint, marginTop: '2px', maxWidth: '62ch', lineHeight: 1.45 },
  chips: { display: 'flex', gap: '4px', flexWrap: 'wrap' },
  // The same control scale as the Expense Analysis panels: 28px high, 11px text.
  chip:  { height: '28px', fontSize: '11px', fontFamily: 'inherit', padding: '0 10px', borderRadius: '6px', borderWidth: '1px', borderStyle: 'solid', borderColor: c.border, background: c.surface, color: c.textMuted, cursor: 'pointer' },
  chipOn:{ background: c.navy, borderColor: c.navy, color: c.surface, fontWeight: 600 },
  rows:  { display: 'grid', gap: '6px', padding: '12px 16px 16px' },
  row:   {
    display: 'grid', gridTemplateColumns: 'minmax(92px, 148px) 1fr minmax(92px, auto)', gap: '12px',
    alignItems: 'center', width: '100%', textAlign: 'left', fontFamily: 'inherit',
    background: 'none', border: 'none', borderRadius: '5px', padding: '3px 4px', cursor: 'pointer',
  },
  rowOn: { background: c.surfaceSunk, boxShadow: `inset 3px 0 0 ${c.navy}` },
  name:  { fontSize: '12px', color: c.textStrong, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  track: { position: 'relative', display: 'block', height: '14px', background: c.surfaceSunk, borderRadius: '3px' },
  fill:  { display: 'block', height: '100%', borderRadius: '3px' },
  // The company line, in the brand red the board keeps for reference marks
  // rather than for good or bad.
  avg:   { position: 'absolute', top: '-4px', bottom: '-4px', width: '2px', background: c.brand },
  value: { fontSize: '12px', fontWeight: 600, color: c.text, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  share: { display: 'block', fontSize: '10px', fontWeight: 400, color: c.textFaint },
};
